import {
  Checkbox,
  Collapse,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@material-ui/core';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { cloneDeep } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

import { ELiveDataMetricsKey, ELiveDataMetricsString } from '~/src/dash/api/live_data/models';
import {
  EMonitorMetricDirection,
  EMonitorObjectType,
  EMonitorTriggerType,
  EMonitorType,
  IMonitor,
  IMonitorAlertHistory,
} from '~/src/dash/api/monitors/monitors_models';
import {
  MonitorObjectTypeToStringV2,
  MonitorTriggerTypeToString,
} from '~/src/dash/api/monitors/monitors_utils';
import { EBool } from '~/src/dash/api/types/bool';
import { MenuButton, renderMenuTitle } from '~/src/dash/components/menu-button';
import { ModelSecondaryButton } from '~/src/dash/components/model-secondary-button';
import { MonitorNotificationsHistory } from '~/src/dash/components/monitor/monitor-notifications-history';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IStore } from '~/src/dash/reducers/store';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  RouteBidderSettingsExisting,
  RouteBidderSettingsList,
  RouteCampaignExisting,
  RouteChannelExisting,
  RouteChannelsView,
  RouteNetworkSettingsExisting,
} from '~/src/dash/router/routes';
import { IListItem } from '~/src/dash/types/menuList';
import { ISessionStateProps, IWithStylesProps } from '~/src/dash/types/props';
import {
  ChipDelivering,
  ChipInactive,
  ChipPending,
  ChipUntargeted,
} from '~/src/dash/utils/campaign-status';
import { decorate } from '~/src/dash/utils/decorator';

export interface IProps extends ISessionStateProps, IWithStylesProps {
  monitor: IMonitor;
  session: ISessionState;
  user: IUserState;
  loading: boolean;
  handleAcknowledgeMonitorAlerts: (alertIDs: string[], acknowledgeAlerts: boolean) => void;
}

function triggerTypeToChip(m: EMonitorTriggerType): any {
  switch (m) {
    case EMonitorTriggerType.MonitorTriggerType_ALERT:
      return ChipUntargeted;
    case EMonitorTriggerType.MonitorTriggerType_WARNING:
      return ChipPending;
    case EMonitorTriggerType.MonitorTriggerType_ALERT_RECOVERY:
      return ChipDelivering;
    case EMonitorTriggerType.MonitorTriggerType_WARNING_RECOVERY:
      return ChipDelivering;
    case EMonitorTriggerType.MonitorTriggerType_SHUTDOWN:
      return ChipUntargeted;
  }
  return ChipDelivering;
}

function MonitorHistoryComp(props: IProps) {
  const { monitor } = props;
  const [openCollapse, setOpenCollapse] = useState(new Map<number, boolean>());
  const [filteredMonitorAlerts, setFilteredMonitorAlerts] = useState([] as IMonitorAlertHistory[]);
  const [readStatus, setReadStatus] = useState('All');
  const [statusFilter, setStatusFilter] = useState(-1);
  const [forceMenuButtonClosed, setForceMenuButtonClosed] = useState(false);

  // if the filters change or the alert histories change
  useEffect(() => {
    filterMonitorAlerts();
  }, [statusFilter, readStatus, monitor.AlertHistory]);

  const filterMonitorAlerts = () => {
    if (monitor === undefined || monitor.AlertHistory === undefined) {
      return;
    }
    let filteredMonitorAlerts = cloneDeep(monitor.AlertHistory).filter((alert) => {
      if (readStatus === 'All') {
        return true;
      } else if (readStatus === 'Read') {
        return alert.AcknowledgedAt !== '' && alert.AcknowledgedAt !== null;
      } else {
        return alert.AcknowledgedAt === '' || alert.AcknowledgedAt === null;
      }
    });

    if (statusFilter !== -1) {
      filteredMonitorAlerts = filteredMonitorAlerts.filter(
        (alert) => alert.TriggerType === statusFilter,
      );
    }

    setFilteredMonitorAlerts(filteredMonitorAlerts);
  };

  const handleClickLink = (listItem: IListItem) => {
    if (
      monitor.TargetedObjectType === EMonitorObjectType.MonitorObjectType_CHANNEL_GROUP &&
      monitor.MonitorType === EMonitorType.MonitorType_SINGLE
    ) {
      // navigate to channel group
      window.location.href =
        '/#' +
        getRoutePath(
          RouteChannelsView,
          extractRouteParams({ user: props.user, session: props.session }),
        ) +
        `?channel_group_id=${listItem.ID}`;
    } else if (
      (monitor.TargetedObjectType === EMonitorObjectType.MonitorObjectType_CHANNEL_GROUP &&
        monitor.MonitorType === EMonitorType.MonitorType_MULTI) ||
      monitor.TargetedObjectType === EMonitorObjectType.MonitorObjectType_SITE
    ) {
      // navigate to channel
      navigate(
        RouteChannelExisting,
        extractRouteParams({ user: props.user, session: props.session }),
        { ch: listItem.ID },
      );
    } else if (
      monitor.TargetedObjectType === EMonitorObjectType.MonitorObjectType_BIDDER_GROUP &&
      monitor.MonitorType === EMonitorType.MonitorType_SINGLE
    ) {
      // navigate to bidder group
      window.location.href =
        '/#' +
        getRoutePath(
          RouteBidderSettingsList,
          extractRouteParams({ user: props.user, session: props.session }),
        ) +
        `?bidder_group_id=${listItem.ID}`;
    } else if (
      (monitor.TargetedObjectType === EMonitorObjectType.MonitorObjectType_BIDDER_GROUP &&
        monitor.MonitorType === EMonitorType.MonitorType_MULTI) ||
      monitor.TargetedObjectType === EMonitorObjectType.MonitorObjectType_BIDDER
    ) {
      // navigate to bidder
      navigate(
        RouteBidderSettingsExisting,
        extractRouteParams({ user: props.user, session: props.session }),
        { b: listItem.ID },
      );
    } else if (monitor.TargetedObjectType === EMonitorObjectType.MonitorObjectType_CAMPAIGN) {
      // navigate to campaign
      if (listItem.orderID) {
        navigate(
          RouteCampaignExisting,
          extractRouteParams({ user: props.user, session: props.session }),
          {
            campaignID: listItem.ID,
            order: listItem.orderID.toString(),
          },
        );
      }
    } else if (monitor.TargetedObjectType === EMonitorObjectType.MonitorObjectType_NETWORK) {
      // navigate to network
      navigate(
        RouteNetworkSettingsExisting,
        extractRouteParams({ user: props.user, session: props.session }),
        { networkID: listItem.ID },
      );
    }
  };

  const renderReadDropdown = () => {
    return (
      <Grid item style={{ width: '100%' }}>
        <Grid container direction='row' alignItems='center' spacing={2}>
          <Grid item xs={3}>
            <Typography style={{ fontWeight: 500, fontSize: 12 }}>Read Status</Typography>
          </Grid>
          <Grid item xs={9}>
            <Select
              name='Read'
              value={readStatus}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReadStatus(e.target.value)}
            >
              <MenuItem key='All' value='All'>
                All
              </MenuItem>
              <MenuItem key='Read' value='Read'>
                Read
              </MenuItem>
              <MenuItem key='Not Read' value='Not Read'>
                Not Read
              </MenuItem>
            </Select>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderAlertDropdown = () => {
    return (
      <Grid item style={{ width: '100%' }}>
        <Grid container direction='row' alignItems='center' spacing={2}>
          <Grid item xs={3}>
            <Typography style={{ fontWeight: 500, fontSize: 12 }}>Status</Typography>
          </Grid>
          <Grid item xs={9}>
            <Select
              name='Status'
              value={statusFilter}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setStatusFilter(parseInt(e.target.value, 10))
              }
            >
              <MenuItem key='All' value={-1}>
                All
              </MenuItem>
              <MenuItem key='Alert' value={EMonitorTriggerType.MonitorTriggerType_ALERT}>
                Alert
              </MenuItem>
              <MenuItem
                key='AlertRecovery'
                value={EMonitorTriggerType.MonitorTriggerType_ALERT_RECOVERY}
              >
                Alert Recovery
              </MenuItem>
              <MenuItem key='Warning' value={EMonitorTriggerType.MonitorTriggerType_WARNING}>
                Warning
              </MenuItem>
              <MenuItem
                key='WarningRecovery'
                value={EMonitorTriggerType.MonitorTriggerType_WARNING_RECOVERY}
              >
                Warning Recovery
              </MenuItem>
              <MenuItem key='Shutdown' value={EMonitorTriggerType.MonitorTriggerType_SHUTDOWN}>
                Shutdown
              </MenuItem>
            </Select>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderSingleEvent = (event: IMonitorAlertHistory) => {
    const Chip = triggerTypeToChip(event.TriggerType);
    const metricName = ELiveDataMetricsString(
      ELiveDataMetricsKey.get(event.TargetedMetric) || ('' as any),
    );
    const publisherTimezone = props.session.publisher.Timezone;
    const alertAcknowledged = event.AcknowledgedAt !== '' && event.AcknowledgedAt !== null;

    return (
      <Paper
        classes={{ root: props.classes.paper }}
        style={{ marginLeft: 32, width: '80%', marginBottom: '20px' }}
      >
        <div style={{ width: '100%', padding: '20px' }}>
          <Grid item>
            <Grid container direction='row' justify='space-between' alignItems='center'>
              <Grid item>
                {event.IsTest === EBool.EBool_TRUE ? (
                  <ChipInactive label={'Test'} />
                ) : (
                  <Chip label={MonitorTriggerTypeToString(event.TriggerType)} />
                )}
              </Grid>
              <Grid item>
                <Grid item>
                  <Grid
                    container
                    direction='row'
                    justify='flex-end'
                    alignItems='center'
                    spacing={2}
                  >
                    {alertAcknowledged &&
                      event.AcknowledgedByUserID &&
                      event.AcknowledgedByUserID.Valid && (
                        <Grid item>
                          {`User Name: ${event.AcknowledgedByUserName} at ${event.AcknowledgedAt}`}
                        </Grid>
                      )}
                    <Grid item>{alertAcknowledged ? 'Read' : 'Mark As Read'}</Grid>
                    <Grid item>
                      <Checkbox
                        onClick={() => {
                          props.handleAcknowledgeMonitorAlerts(
                            [event.ID.toString()],
                            !alertAcknowledged,
                          );
                        }}
                        checked={alertAcknowledged}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <p>
            Triggered{' '}
            {moment(event.CreatedAt).tz(publisherTimezone).format('DD-MM-YYYY[\n]HH:mm:ss') +
              '\n' +
              publisherTimezone}{' '}
          </p>
          <p>
            Value was: {event.Value}, threshold was: {event.LimitAtThisMoment}
          </p>
          <p>
            Condition: Trigger {MonitorTriggerTypeToString(event.TriggerType)} if {event.Value}{' '}
            {event.MetricDirection === EMonitorMetricDirection.MonitorMetricDirection_ABOVE
              ? '>'
              : '<'}{' '}
            {event.LimitAtThisMoment} {metricName} for{' '}
            {MonitorObjectTypeToStringV2(
              event.TargetedObjectType,
              monitor.EnableTargetAllActive === EBool.EBool_TRUE,
              monitor.MonitorType,
            )}
            :
            {event.listItems &&
              event.listItems.map((listItem) => {
                return (
                  <Typography
                    style={{
                      fontSize: 14,
                      color: 'rgb(88, 101, 232)',
                      cursor: 'pointer',
                      marginLeft: 3,
                      display: 'inline',
                    }}
                    onClick={() => handleClickLink(listItem)}
                  >
                    {`[${listItem.ID}] ${listItem.title}`}
                  </Typography>
                );
              })}
            {(!event.listItems || event.listItems.length === 0) && ( // this might happen if the channel in a channel group was deleted
              <Typography
                style={{ fontSize: 14, cursor: 'pointer', marginLeft: 3, display: 'inline' }}
              >
                {`[${event.TargetedObject}]`}
              </Typography>
            )}
          </p>
        </div>
        <Divider />
        <div
          style={{
            width: '100%',
            paddingLeft: '20px',
            paddingRight: '20px',
            paddingTop: '5px',
            paddingBottom: '5px',
          }}
        >
          <ModelSecondaryButton
            onClick={() => {
              openCollapse.set(event.ID, !(openCollapse.get(event.ID) || false));
              // need to do a copy because react compare the ref to map
              setOpenCollapse(new Map<number, boolean>(openCollapse));
            }}
          >
            Show notifications{' '}
            {openCollapse.get(event.ID) || false ? <ExpandLess /> : <ExpandMore />}
          </ModelSecondaryButton>
          <Collapse in={openCollapse.get(event.ID) || false} timeout='auto' mountOnEnter>
            <div style={{ paddingTop: '10px' }} />
            <MonitorNotificationsHistory alertID={event.ID} monitor={monitor} />
          </Collapse>
        </div>
      </Paper>
    );
  };

  const readDropdown = {
    closeOnClick: true,
    render: renderReadDropdown,
  };
  const alertDropdown = {
    closeOnClick: true,
    render: renderAlertDropdown,
  };

  const filteredMonitorIDs = filteredMonitorAlerts.map((alert) => alert.ID.toString());
  const readMonitorIDs = filteredMonitorAlerts.filter(
    (alert) => alert.AcknowledgedAt !== null && alert.AcknowledgedAt !== '',
  );
  const allAlertsAcknowledged = filteredMonitorIDs.length === readMonitorIDs.length;

  return (
    <div>
      <Grid item style={{ width: '80%' }}>
        <Grid
          container
          direction='row'
          alignItems='center'
          justify='space-between'
          style={{ marginLeft: 32 }}
        >
          <Grid item style={{ marginBottom: 18 }} onClick={() => setForceMenuButtonClosed(false)}>
            <MenuButton
              forceClosed={forceMenuButtonClosed}
              titleRender={renderMenuTitle}
              items={[readDropdown, alertDropdown]}
              minWidth={450}
              marginTop={60}
              marginLeft={0}
            />
          </Grid>
          <Grid item style={{ marginRight: 20 }}>
            <Grid container direction='row' alignItems='center' justify='space-between'>
              <Grid item>Mark All as Read</Grid>
              <Grid item>
                <Checkbox
                  onClick={() => {
                    props.handleAcknowledgeMonitorAlerts(
                      filteredMonitorIDs,
                      !allAlertsAcknowledged,
                    );
                  }}
                  checked={allAlertsAcknowledged}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {filteredMonitorAlerts.map((value) => {
        return renderSingleEvent(value);
      })}
    </div>
  );
}

const style = (theme: any) => ({
  section: {
    display: 'block',
    marginBottom: theme.spacing.unit * 2,
  },
  button: {
    margin: theme.spacing.unit,
  },
});

export const MonitorHistory = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: style,
    router: true,
  },
  MonitorHistoryComp,
);
